import { intervalLabel } from '@fieldops/shared'
import { useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { money, wholeDollars } from '../../lib/format.js'
import { Icon } from '../Icon.js'
import { centsOf, dollarsOf } from '../proposal/draft.js'
import { dayLabel } from '../repeat/rule.js'
import { Toggle } from '../ui.js'
import { groupAside, headingFor, rowNames, type AgreementPage, type Folding, type ReviewActions } from './AgreementGrid.js'
import { frequencyKey, frequencyOf, nextOn, type AgreementGroup, type GroupBy } from './agreement-view.js'
import { FREQUENCIES } from './builder-model.js'
import { TickBox, useHeld } from './review-parts.js'

type Row = AgreementPage['schedules'][number]

/** One change to some services, as `agreements.patchServices` takes it. */
export type ServicePatch = {
  priceIncTax?: number | null
  intervalCount?: number
  intervalUnit?: Row['intervalUnit']
  firstDueOn?: string
  paused?: boolean
}

/** Saves a patch; the promise rejects when it did not save, and the page says why. */
export type SavePatch = (scheduleIds: readonly string[], patch: ServicePatch) => Promise<unknown>

const COLUMNS = 'grid-cols-[20px_minmax(220px,1fr)_150px_112px_144px_84px_84px_52px]'

/**
 * The agreement as figures: one line per service, with its price, how often,
 * first visit and whether it is running edited where they are drawn. Each
 * change is saved as it is made, through `agreements.patchServices`. The
 * name still opens Edit service, which is where a visit of several services
 * is priced.
 */
export function AgreementTable({
  page,
  groups,
  by,
  actions,
  onSave,
  fold,
}: {
  page: AgreementPage
  groups: readonly AgreementGroup[]
  by: GroupBy
  actions: ReviewActions
  /** Absent for somebody who cannot edit, or on an agreement that has ended. */
  onSave?: SavePatch
  /** As the timeline folds: a folded group is one line of its next visit and worth. */
  fold?: Folding
}) {
  const { selected, onSelect, onEditService, onAddSite } = actions
  const term = { min: page.startsOn, max: page.termEndsOn }

  return (
    <div className="overflow-x-auto">
      <div role="table" aria-label="Sites and services" className="min-w-[900px]">
        <div role="row" className={cn('grid items-end gap-3 border-b border-ink-100 px-1 pb-2', COLUMNS)}>
          <span role="columnheader" aria-label="Select" />
          {[by === 'site' ? 'Site and service' : 'Service and site', 'How often', 'Per visit', 'First visit', 'Next', 'A year', 'Active'].map(
            (heading, index) => (
              <span
                key={heading}
                role="columnheader"
                className={cn(
                  'text-[10px] font-bold tracking-[0.06em] text-ink-400 uppercase',
                  index >= 4 && index <= 5 && 'text-right',
                )}
              >
                {heading}
              </span>
            ),
          )}
        </div>

        {groups.map((group, groupIndex) =>
          fold && !fold.isOpen(group.key) ? (
            <div key={group.key} role="rowgroup">
              <div
                role="row"
                aria-label={group.name}
                className={cn('grid items-center gap-3 px-1 py-1.5', COLUMNS, groupIndex > 0 && 'border-t border-ink-100')}
              >
                <div role="rowheader" className="flex min-w-0" style={{ gridColumn: '1 / span 5' }}>
                  {headingFor(group, by, actions, fold)}
                </div>
                <span role="cell" className="text-right text-[12.5px] font-semibold whitespace-nowrap text-ink-900 tabular-nums">
                  {groupAside(group.rows)}
                </span>
                <span role="cell" className="text-right text-[12.5px] whitespace-nowrap text-ink-500 tabular-nums">
                  {wholeDollars(group.aYear)}
                </span>
                <span role="cell" />
              </div>
            </div>
          ) : (
            <div key={group.key} role="rowgroup">
              <div role="row" className={cn('px-1 pt-4 pb-1.5', groupIndex > 0 && 'border-t border-ink-100')}>
                <div role="rowheader">{headingFor(group, by, actions, fold)}</div>
              </div>
              {group.rows.map((row) => {
                const names = rowNames(row, page.client.id)
                const on = nextOn(row)
                const save = onSave ? (patch: ServicePatch) => onSave([row.id], patch) : undefined
                return (
                  <div
                    key={row.id}
                    role="row"
                    aria-label={names.both}
                    className={cn('grid items-center gap-3 rounded-md px-1 py-[var(--row-pad-y)] hover:bg-ink-50', COLUMNS)}
                  >
                    <span role="cell" className="flex">
                      {selected && onSelect ? (
                        <TickBox checked={selected.has(row.id)} onChange={(checked) => onSelect([row.id], checked)} label={`Select ${names.both}`} />
                      ) : null}
                    </span>
                    <span role="cell" className="min-w-0 pl-2.5">
                      {onEditService ? (
                        <button
                          type="button"
                          onClick={() => onEditService(row)}
                          aria-label={`Edit ${names.both}`}
                          title={names.both}
                          className="group/edit flex w-full min-w-0 items-baseline gap-2 rounded-md px-1.5 py-0.5 text-left hover:bg-ink-100"
                        >
                          <span className="truncate text-[13px] font-medium text-ink-900 group-hover/edit:text-brand-700">
                            {by === 'site' ? names.service : names.site}
                          </span>
                          <Icon
                            name="pencil"
                            className="ml-auto size-3.5 shrink-0 self-center text-ink-400 opacity-0 group-hover/edit:opacity-100 group-focus-visible/edit:opacity-100"
                          />
                        </button>
                      ) : (
                        <span className="block truncate px-1.5 text-[13px] font-medium text-ink-900">
                          {by === 'site' ? names.service : names.site}
                        </span>
                      )}
                    </span>
                    <span role="cell">
                      <FrequencyCell row={row} label={names.both} onSave={save} />
                    </span>
                    <span role="cell">
                      <PriceCell row={row} label={names.both} onSave={save} onEdit={onEditService ? () => onEditService(row) : undefined} />
                    </span>
                    <span role="cell">
                      <FirstVisitCell row={row} label={names.both} term={term} onSave={save} />
                    </span>
                    <span role="cell" className="text-right text-[12.5px] whitespace-nowrap text-ink-700 tabular-nums">
                      {row.paused ? <span className="text-ink-400">Paused</span> : on ? dayLabel(on, { year: false }) : '—'}
                    </span>
                    <span role="cell" className="text-right text-[12.5px] whitespace-nowrap text-ink-500 tabular-nums">
                      {wholeDollars(row.aYear)}
                    </span>
                    <span role="cell" className="flex">
                      <ActiveCell row={row} label={names.both} onSave={save} />
                    </span>
                  </div>
                )
              })}
            </div>
          ),
        )}

        {onAddSite ? (
          <div className="border-t border-ink-100 pt-2">
            <button
              type="button"
              onClick={onAddSite}
              className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[13px] font-medium text-brand-700 hover:bg-brand-50"
            >
              <Icon name="plus" className="size-4" />
              Add a site
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

/** A cell's control: quiet until it is hovered or in use. */
const cellControl =
  'h-8 w-full rounded-md border border-transparent bg-transparent px-2 text-[12.5px] text-ink-900 tabular-nums hover:border-ink-200 focus:border-brand-400 focus:bg-white focus:outline-none disabled:hover:border-transparent'

function FrequencyCell({ row, label, onSave }: { row: Row; label: string; onSave?: (patch: ServicePatch) => Promise<unknown> }) {
  const [shown, hold, release] = useHeld(frequencyKey(row))
  const options = FREQUENCIES.some((option) => frequencyKey(option) === shown)
    ? FREQUENCIES
    : [...FREQUENCIES, frequencyOf(shown)]
  return (
    <select
      aria-label={`How often for ${label}`}
      value={shown}
      disabled={!onSave}
      onChange={(event) => {
        const next = event.target.value
        hold(next)
        onSave?.(frequencyOf(next)).catch(release)
      }}
      className={cn(cellControl, 'cursor-pointer disabled:cursor-default')}
    >
      {options.map((option) => (
        <option key={frequencyKey(option)} value={frequencyKey(option)}>
          {intervalLabel(option)}
        </option>
      ))}
    </select>
  )
}

/**
 * The price per visit, typed as dollars. A visit of several services has a
 * price per service, so it is drawn as their total and opens Edit service.
 */
function PriceCell({
  row,
  label,
  onSave,
  onEdit,
}: {
  row: Row
  label: string
  onSave?: (patch: ServicePatch) => Promise<unknown>
  onEdit?: () => void
}) {
  const single = row.services.length === 1
  const [shown, hold, release] = useHeld(single ? (row.services[0]!.priceIncTax ?? null) : row.priceIncTax)
  const [text, setText] = useState<string | null>(null)
  const cancelled = useRef(false)

  if (!single || !onSave) {
    const figure = shown ? money(shown) : 'No price'
    return !single && onEdit ? (
      <button
        type="button"
        onClick={onEdit}
        title="Several services on this visit: each is priced in Edit service."
        className={cn(cellControl, 'text-left')}
      >
        {figure}
      </button>
    ) : (
      <span className={cn('block px-2 text-[12.5px] tabular-nums', shown ? 'text-ink-900' : 'text-ink-400')}>{figure}</span>
    )
  }

  const commit = () => {
    if (text === null) return
    setText(null)
    if (cancelled.current) return
    const cents = centsOf(text)
    // Words that are not a figure put the price back rather than clearing it.
    if (cents === null && text.trim() !== '') return
    if (cents === shown) return
    hold(cents)
    onSave({ priceIncTax: cents }).catch(release)
  }

  return (
    <input
      aria-label={`Price per visit for ${label}`}
      inputMode="decimal"
      value={text ?? (shown === null ? '' : money(shown))}
      placeholder="No price"
      onFocus={(event) => {
        cancelled.current = false
        setText(dollarsOf(shown))
        event.currentTarget.select()
      }}
      onChange={(event) => setText(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') {
          cancelled.current = true
          setText(null)
          event.currentTarget.blur()
        }
      }}
      className={cn(cellControl, 'placeholder:text-ink-400')}
    />
  )
}

function FirstVisitCell({
  row,
  label,
  term,
  onSave,
}: {
  row: Row
  label: string
  term: { min: string; max: string }
  onSave?: (patch: ServicePatch) => Promise<unknown>
}) {
  const [shown, hold, release] = useHeld(row.firstDueOn)
  const [draft, setDraft] = useState<string | null>(null)
  const cancelled = useRef(false)

  const commit = () => {
    if (draft === null) return
    setDraft(null)
    if (cancelled.current || !draft || draft === shown) return
    hold(draft)
    onSave?.({ firstDueOn: draft }).catch(release)
  }

  return (
    <input
      type="date"
      aria-label={`First visit for ${label}`}
      min={term.min}
      max={term.max}
      value={draft ?? shown}
      disabled={!onSave}
      onFocus={() => {
        cancelled.current = false
      }}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur()
        if (event.key === 'Escape') {
          cancelled.current = true
          setDraft(null)
          event.currentTarget.blur()
        }
      }}
      className={cellControl}
    />
  )
}

function ActiveCell({ row, label, onSave }: { row: Row; label: string; onSave?: (patch: ServicePatch) => Promise<unknown> }) {
  const [running, hold, release] = useHeld(row.stoppedAt === null)
  return (
    <Toggle
      size="sm"
      checked={running}
      disabled={!onSave}
      label={`${label} is active`}
      onChange={(next) => {
        hold(next)
        onSave?.({ paused: !next }).catch(release)
      }}
    />
  )
}
